import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useQuery, type QueryFunction, type QueryKey } from '@tanstack/react-query';
import { getCurrentOrganizationId, useCurrentUser, useUserWorkspaces } from '@/entities/user';
import {
  getWorkspaceId,
  setWorkspaceId,
  useCurrentWorkspaceId,
  WORKSPACE_SLUG_KEY,
} from '@/entities/workspace';
import { ENV } from '@/lib/config/env';
import { notificationService } from '@/lib/services/notification.service';
import { ROUTES } from '@/shared/config/routes';
import { canNavigateBack } from '@/shared/utils/canNavigateBack';

/**
 * Wire contract every entity family's `GET /:id/resolve-workspace` endpoint shares.
 * Entity APIs return supersets of this shape (extra fields pass through
 * `cross-org`'s `resolution` untouched); conformance is enforced where the
 * consumer hands its query to `useDeepLinkResolution`.
 */
export interface WorkspaceResolution {
  sameOrg: boolean;
  workspaceId: string;
  workspaceSlug: string;
  organizationSlug: string;
  organizationName: string;
  /**
   * Caller's access verdict. Present only for same-org resolutions of
   * published entities (the platform permission check is org-scoped);
   * `null` means no access, absent means "unknown — let the post-switch
   * workspace-scoped fetch decide".
   */
  accessLevel?: string | null;
}

export type DeepLinkResolutionState =
  | { status: 'resolving' }
  | { status: 'ready' }
  | { status: 'access-denied' }
  | { status: 'not-found' }
  | {
      status: 'cross-org';
      resolution: WorkspaceResolution;
      /** Whether the tab has somewhere to go back to (vs bail out to Home). */
      canGoBack: boolean;
      switchAndOpen: () => void;
      cancel: () => void;
    };

/**
 * The three terminal outcomes the interstitial renders (cross-org prompt,
 * access-denied, generic not-available). Single source for the union so a
 * future terminal state is a one-site change, not a per-page edit.
 */
export type TerminalResolutionState = Extract<
  DeepLinkResolutionState,
  { status: 'cross-org' | 'access-denied' | 'not-found' }
>;

/** Narrows a resolution state to the terminal outcomes handled by the interstitial. */
export function isTerminalResolution(
  state: DeepLinkResolutionState,
): state is TerminalResolutionState {
  return (
    state.status === 'cross-org' || state.status === 'access-denied' || state.status === 'not-found'
  );
}

export interface UseDeepLinkResolutionParams<TQueryKey extends QueryKey = QueryKey> {
  /**
   * Query options resolving the deep-linked entity id to its owning
   * workspace. Injected (rather than imported) so `shared/` stays decoupled
   * from entity API modules — pass the family's query factory result, e.g.
   * `projectResolutionQuery(projectId)`. Spread into `useQuery` verbatim, so
   * factory-set options (staleTime, retry, …) are honored; only `enabled` is
   * overridden by the seam's own gate.
   */
  resolutionQuery: {
    queryKey: TQueryKey;
    // Optional to mirror TanStack's `queryOptions()` output (its queryFn is
    // optional), so factory results assign without casts. `useQuery` accepts
    // the same shape.
    queryFn?: QueryFunction<WorkspaceResolution, TQueryKey>;
  };
  /** Consumer guard for the resolve call (e.g. the URL id is present). */
  enabled: boolean;
}

/**
 * Bounce URL for the Platform re-auth flow. Platform's
 * `/login/organizations/:slug` route auto-logs an authenticated caller into
 * the target org (the `email` param skips its email step) and then redirects
 * to `redirectUri` verbatim. The workspace-slug param on the deep link makes
 * boot select the owning workspace when the bounce lands back here.
 */
function buildCrossOrgBounceUrl(resolution: WorkspaceResolution, sessionEmail: string): string {
  const entityHref = `${window.location.origin}${window.location.pathname}`;
  // Slugs are platform-generated ([a-z0-9-]), but encode them anyway so a
  // hostile value can't reshape the bounce path or split the landing query.
  const workspaceSlug = encodeURIComponent(resolution.workspaceSlug);
  const organizationSlug = encodeURIComponent(resolution.organizationSlug);
  const redirectUri = `${entityHref}?${WORKSPACE_SLUG_KEY}=${workspaceSlug}`;
  return (
    `${ENV.PLATFORM_BASE_URL}/login/organizations/${organizationSlug}` +
    `?email=${encodeURIComponent(sessionEmail)}&redirectUri=${encodeURIComponent(redirectUri)}`
  );
}

/**
 * Deep-link seam for entity routes (projects, guidelines folders, brief
 * templates, …): resolves the URL's entity id to its owning workspace BEFORE
 * the workspace-scoped entity fetch runs, so an id from another workspace
 * doesn't 404 under the ambient `x-selected-workspace-id` header.
 *
 * Outcomes:
 *   - `ready` — the active workspace owns the entity; the caller may run its
 *     entity fetch.
 *   - Same-org entity in ANOTHER workspace the caller can access → silently
 *     switches via the same `setWorkspaceId` as the manual switcher (info
 *     toast, no reload), then settles on `ready`.
 *   - `access-denied` — same-org published entity the caller has no access
 *     to (`accessLevel: null`); no switch.
 *   - `not-found` — the resolver 404ed (nonexistent id OR an entity the
 *     caller may not know exists; the backend keeps those indistinguishable).
 *   - `cross-org` — NEVER switches silently: an org switch rotates the shared
 *     httpOnly session cookie and flips every open tab. Exposes the
 *     resolution payload plus `switchAndOpen` (full navigation through the
 *     Platform re-auth bounce) and a history-aware `cancel` for the
 *     `DeepLinkInterstitial` prompt.
 *
 * Because resolution re-runs on every mount, a URL opened in a tab whose
 * localStorage points at another workspace self-heals automatically.
 */
export function useDeepLinkResolution<TQueryKey extends QueryKey = QueryKey>(
  params: UseDeepLinkResolutionParams<TQueryKey>,
): DeepLinkResolutionState {
  const { t } = useTranslation('common');
  const activeWorkspaceId = useCurrentWorkspaceId();
  const workspaces = useUserWorkspaces();
  const sessionEmail = useCurrentUser()?.email;
  const navigate = useNavigate();

  // Spread the factory's full options so tuning it (staleTime, retry, …) takes
  // effect here; only `enabled` is owned by the seam, so it wins last.
  const { data: resolution, isError } = useQuery({
    ...params.resolutionQuery,
    enabled: params.enabled,
  });

  // An absent accessLevel (unpublished entity) still switches: the
  // post-switch workspace-scoped fetch enforces access for those. Only the
  // explicit `null` verdict (published, no access) blocks the switch.
  const needsSwitch =
    !!resolution?.sameOrg &&
    resolution.accessLevel !== null &&
    resolution.workspaceId !== activeWorkspaceId;

  useEffect(() => {
    // `needsSwitch` also proves `resolution` is defined (aliased narrowing).
    if (!needsSwitch) return;
    // Re-check against the store: the effect can re-run (StrictMode,
    // re-renders) after the switch already happened.
    if (getWorkspaceId() === resolution.workspaceId) return;

    setWorkspaceId(resolution.workspaceId, getCurrentOrganizationId());

    const workspaceName =
      workspaces.find((ws) => ws.id === resolution.workspaceId)?.name ?? resolution.workspaceSlug;
    notificationService.showInfo(
      t('hooks.useDeepLinkResolution.switchedToWorkspace', { workspaceName }),
    );
  }, [needsSwitch, resolution, workspaces, t]);

  // Trust cached resolution data over a failed BACKGROUND refetch (e.g. a
  // focus refetch erroring after staleTime): only a failure with nothing
  // cached means the entity is genuinely unavailable — same as the switch
  // effect, which already acts on cached data.
  if (isError && !resolution) {
    return { status: 'not-found' };
  }
  if (!resolution) {
    return { status: 'resolving' };
  }
  if (!resolution.sameOrg) {
    const canGoBack = canNavigateBack();
    return {
      status: 'cross-org',
      resolution,
      canGoBack,
      switchAndOpen: () => {
        window.location.assign(buildCrossOrgBounceUrl(resolution, sessionEmail ?? ''));
      },
      cancel: () => {
        // In-app history → return to the prior view with zero context loss;
        // fresh tab / pasted URL → Home is the only sensible destination.
        if (canGoBack) {
          void navigate(-1);
        } else {
          void navigate(ROUTES.HOME);
        }
      },
    };
  }
  if (resolution.accessLevel === null) {
    return { status: 'access-denied' };
  }
  if (resolution.workspaceId !== activeWorkspaceId) {
    // Switch effect is in flight; the store update re-renders us into `ready`.
    return { status: 'resolving' };
  }
  return { status: 'ready' };
}
